<template>
	<div class="icon-container" :class="containerClasses">
		<span :class="classNames"
			:tabindex="isTabbable"
			:aria-label="ariaLabel"
			@click=emitClick
			@keydown.enter="emitClick">

			<font-awesome-icon v-if="!isCustom"
				:icon="setIcon(icon)"
				:class="style(type)"
				:aria-label="`${icon} icon`"
				:alt="`${icon} icon`"
				:size="resolveSize(size)" />

			<svg-icon v-if="isCustom"
				:classNames="style(type)"
				:Icon="setIcon(icon)"
				:size="size"
				:editStroke="editStroke"
				:name="icon"
				:fill="fill">
				<slot></slot>
			</svg-icon>
		</span>
		<slot name="label"></slot>
	</div>
</template>

<script>
	// TODO split this up into separate files
	import { library } from '@fortawesome/fontawesome-svg-core';
	import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
	import { isEmpty } from 'lodash';

	import { faShoppingCart } from '@fortawesome/pro-light-svg-icons/faShoppingCart';
	import { faShoppingCart as faShoppingCartSolid } from '@fortawesome/free-solid-svg-icons/faShoppingCart';
	import { faCartPlus } from '@fortawesome/free-solid-svg-icons/faCartPlus';
	import { faPlusCircle } from '@fortawesome/pro-light-svg-icons/faPlusCircle';
	import { faMinusCircle } from '@fortawesome/pro-light-svg-icons/faMinusCircle';
	import { faPie } from '@fortawesome/pro-light-svg-icons/faPie';
	import { faMugHot } from '@fortawesome/free-solid-svg-icons/faMugHot';
	import { faShoppingBag } from '@fortawesome/pro-light-svg-icons/faShoppingBag';
	import { faSmileBeam } from '@fortawesome/pro-light-svg-icons/faSmileBeam';
	import { faCar } from '@fortawesome/pro-light-svg-icons/faCar';
	import { faSearchLocation } from '@fortawesome/pro-light-svg-icons/faSearchLocation';
	import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
	import { faLocationCircle } from '@fortawesome/pro-solid-svg-icons/faLocationCircle';
	import { faCircle } from '@fortawesome/pro-light-svg-icons/faCircle';
	import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
	import { faCheckCircle as faCheckCircleLight } from '@fortawesome/pro-light-svg-icons/faCheckCircle';
	import { faTimesCircle } from '@fortawesome/pro-light-svg-icons/faTimesCircle';
	import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons/faMapMarkerAlt';
	import { faLockAlt } from '@fortawesome/pro-light-svg-icons/faLockAlt';
	import { faBars } from '@fortawesome/pro-light-svg-icons/faBars';
	import { faTimes } from "@fortawesome//pro-light-svg-icons/faTimes";
	import { faLongArrowLeft } from '@fortawesome/pro-solid-svg-icons/faLongArrowLeft';
	import { faLongArrowRight } from '@fortawesome/pro-solid-svg-icons/faLongArrowRight';
	import { faCaretRight } from '@fortawesome/pro-solid-svg-icons/faCaretRight';
	import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
	import { faCaretUp } from '@fortawesome/pro-solid-svg-icons/faCaretUp';
	import { faSyncAlt } from '@fortawesome/pro-regular-svg-icons/faSyncAlt';
	import { faSatelliteDish } from '@fortawesome/free-solid-svg-icons/faSatelliteDish';
	import { faEnvelope } from '@fortawesome/pro-solid-svg-icons/faEnvelope';
	import { faPhone } from '@fortawesome/pro-solid-svg-icons/faPhone';
	import { faUserEdit } from '@fortawesome/pro-solid-svg-icons/faUserEdit';
	import { faUserCircle } from '@fortawesome/pro-light-svg-icons/faUserCircle';
	import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';

	// Brands
	import { faInstagram as Instagram } from "@fortawesome/free-brands-svg-icons/faInstagram";
	import { faFacebookF as Facebook } from "@fortawesome/free-brands-svg-icons/faFacebookF";
	import { faTwitter as Twitter } from "@fortawesome/free-brands-svg-icons/faTwitter";
	import { faPinterest as Pinterest } from "@fortawesome/free-brands-svg-icons/faPinterest";
	import { faGooglePlusG as GooglePlus } from "@fortawesome/free-brands-svg-icons/faGooglePlusG";
	import { faLinkedinIn as LinkedIn } from "@fortawesome/free-brands-svg-icons/faLinkedinIn";

	library.add(Instagram, Facebook, Twitter, Pinterest, GooglePlus, LinkedIn);
	library.add(faShoppingCart);
	library.add(faShoppingCartSolid);
	library.add(faCartPlus);
	library.add(faPlusCircle);
	library.add(faMinusCircle);
	library.add(faPie);
	library.add(faMugHot);
	library.add(faShoppingBag);
	library.add(faSmileBeam);
	library.add(faCar);
	library.add(faSearchLocation);
	library.add(faSearch);
	library.add(faLocationCircle);
	library.add(faCircle);
	library.add(faCheckCircle);
	library.add(faCheckCircleLight);
	library.add(faTimesCircle);
	library.add(faMapMarkerAlt);
	library.add(faLockAlt);
	library.add(faBars);
	library.add(faTimes);
	library.add(faLongArrowLeft);
	library.add(faLongArrowRight);
	library.add(faCaretRight);
	library.add(faCaretDown);
	library.add(faCaretUp);
	library.add(faSyncAlt);
	library.add(faSatelliteDish);
	library.add(faEnvelope);
	library.add(faPhone);
	library.add(faUserEdit);
	library.add(faUserCircle);
	library.add(faUsers);

	//Custom
	import SvgIcon from "@/components/common/SvgIcon";
	import Baking from "@/assets/icons/pizza-tracker/baking.svg";
	import Eating from "@/assets/icons/pizza-tracker/eating.svg";
	import Making from "@/assets/icons/pizza-tracker/making.svg";
	import OnItsWay from "@/assets/icons/pizza-tracker/on-its-way.svg";
	import OrderPlaced from "@/assets/icons/pizza-tracker/order-placed.svg";
	import Pickup from "@/assets/icons/pizza-tracker/pickup.svg";
	import Bag from '@/assets/icons/order-tracker/bag.svg';
	import Cart from '@/assets/icons/order-tracker/cart.svg';
	import Glove from '@/assets/icons/order-tracker/glove.svg';
	import House from '@/assets/icons/order-tracker/house.svg';
	import Car from '@/assets/icons/order-tracker/car.svg';
	import Radar from '@/assets/icons/order-tracker/radar.svg';
	import Yum from '@/assets/icons/order-tracker/yum.svg';
	import DeliveryServiceType from "@/assets/icons/service-type/delivery-car.svg";
	import PickupServiceType from "@/assets/icons/service-type/pickup.svg";
	import PizzaFull from '@/assets/icons/menu/pizza-full.svg';
	import PizzaLeft from '@/assets/icons/menu/pizza-left.svg';
	import PizzaRight from '@/assets/icons/menu/pizza-right.svg';
	import PizzaFullInactive from '@/assets/icons/menu/pizza-full-inactive.svg';
	import PizzaLeftInactive from '@/assets/icons/menu/pizza-left-inactive.svg';
	import PizzaRightInactive from '@/assets/icons/menu/pizza-right-inactive.svg';
	import SandFull from '@/assets/icons/menu/sand-full.svg';
	import SandFullInactive from '@/assets/icons/menu/sand-full-inactive.svg';
	import Pie from '@/assets/icons/store/pie.svg';
	import CoffeeCup from '@/assets/icons/store/coffee-cup.svg';
	import RadioButtonSelected from '@/assets/icons/common/radio-button-selected.svg';
	import GreenCheck from '@/assets/icons/common/green-check.svg';
	import CheckMark from '@/assets/icons/common/check-mark.svg';
	import BackChevron from '@/assets/icons/common/back.svg';
	import Redo from '@/assets/icons/common/redo.svg';
	import Close from '@/assets/icons/common/close.svg';
	import Mobile from '@/assets/icons/common/mobile.svg';
	import Account from '@/assets/icons/common/account.svg';
	import TPLogo from '@/assets/icons/logos/tp-logo.svg';

	export const ICON_NAMES = {
		BARS: 'bars',
		CART_ICON: 'cartIcon',
		PLUS_ICON: 'plusIcon',
		MINUS_ICON: 'minusIcon',
		CHECK_CIRCLE: 'checkCircle',
		TIMES_CIRCLE: 'timesCircle',
		TIMES: 'times',
		LOCK: 'lock',
		LOCATION_MARKER: 'locationMarker',
		PIZZA_TRACKER: {
			ORDER_PLACED: "orderPlaced",
			MAKING: 'making',
			BAKING: 'baking',
			ON_ITS_WAY: "onItsWay",
			PICK_UP: 'pickup',
			EATING: 'eating'
		},
		SOCIAL: {
			FACEBOOK: "facebook",
			TWITTER: "twitter",
			PINTEREST: "pinterest",
			GOOGLE_PLUS: "googlePlus",
			LINKEDIN: "linkedIn",
			INSTAGRAM: "instagram"

		},
		SERVICE_TYPE: {
			'delivery': "deliveryServiceType",
			'takeout': "pickupServiceType",
		}
	};

	export default {
		props: {
			'icon': {
				type: String,
				default: 'faCircle',
			},
			'size': {
				type: String
			},
			'classNames': {
				type: Array,
				default: () => {
					return [];
				},
			},
			containerClasses: {
				type: Object,
				default: () => {
					return {};
				},
			},
			'isCustom': {
				type: Boolean,
				default: false
			},
			'editStroke': {
				type: Boolean,
				default: false
			},
			fill: {
				type: Boolean,
				default: true,
			},
			ariaLabel: {
				type: String,
				default: '',
			},
			type: {
				type: String,
				default: '',
			},
		},
		components: {
			FontAwesomeIcon,
			'svg-icon': SvgIcon,
			Baking
		},
		computed: {
			isTabbable() {
				if (this.ariaLabel !== '') {
					return '0';
				}
				return '-1';
			},
			bars() {
				return faBars;
			},
			cartIcon () {
				return faShoppingCart;
			},
			solidCart() {
				return faShoppingCartSolid;
			},
			cartPlus() {
				return faCartPlus;
			},
			plusIcon() {
				return faPlusCircle;
			},
			minusIcon() {
				return faMinusCircle;
			},
			pieAlt() {
				return faPie;
			},
			shoppingBag() {
				return faShoppingBag;
			},
			smile() {
				return faSmileBeam;
			},
			car() {
				return faCar;
			},
			search() {
				return faSearch;
			},
			locationSearch() {
				return faSearchLocation;
			},
			locationCircle() {
				return faLocationCircle;
			},
			circle() {
				return faCircle;
			},
			checkCircle() {
				return faCheckCircle;
			},
			checkCircleLight() {
				return faCheckCircleLight;
			},
			pizzaFull() {
				return PizzaFull;
			},
			pizzaLeft() {
				return PizzaLeft;
			},
			pizzaRight() {
				return PizzaRight;
			},
			pizzaFullInactive() {
				return PizzaFullInactive;
			},
			pizzaLeftInactive() {
				return PizzaLeftInactive;
			},
			pizzaRightInactive() {
				return PizzaRightInactive;
			},
			sandFull() {
				return SandFull;
			},
			sandFullInactive() {
				return SandFullInactive;
			},
			sandLeft() {
				return GreenCheck;
			},
			sandLeftInactive() {
				return GreenCheck;
			},
			sandRight() {
				return GreenCheck;
			},
			sandRightInactive() {
				return GreenCheck;
			},
			pie() {
				return Pie;
			},
			coffee() {
				return CoffeeCup;
			},
			radioButtonSelected() {
				return RadioButtonSelected;
			},
			greenCheck() {
				return GreenCheck;
			},
			checkMark() {
				return CheckMark;
			},
			close() {
				return Close;
			},
			timesCircle() {
				return faTimesCircle;
			},
			times() {
				return faTimes;
			},
			lock() {
				return faLockAlt;
			},
			locationMarker() {
				return faMapMarkerAlt;
			},
			baking() {
				return Baking;
			},
			eating() {
				return Eating;
			},
			making() {
				return Making;
			},
			onItsWay() {
				return OnItsWay;
			},
			delivering() {
				return Car;
			},
			orderPlaced() {
				return OrderPlaced;
			},
			bag() {
				return Bag;
			},
			cart() {
				return Cart;
			},
			glove() {
				return Glove;
			},
			house() {
				return House;
			},
			backChevron() {
				return BackChevron;
			},
			forwardChevron() {
				return faChevronRight;
			},
			backArrow() {
				return faLongArrowLeft;
			},
			forwardArrow() {
				return faLongArrowRight;
			},
			caretRight() {
				return faCaretRight;
			},
			caretDown() {
				return faCaretDown;
			},
			caretUp() {
				return faCaretUp;
			},
			sync() {
				return faSyncAlt;
			},
			redo() {
				return Redo;
			},
			satellite() {
				return faSatelliteDish;
			},
			radar() {
				return Radar;
			},
			yum() {
				return Yum;
			},
			pickup() {
				return Pickup;
			},
			facebook() {
				return Facebook;
			},
			twitter() {
				return Twitter;
			},
			pinterest() {
				return Pinterest;
			},
			googlePlus() {
				return GooglePlus;
			},
			linkedIn() {
				return LinkedIn;
			},
			instagram() {
				return Instagram;
			},
			envelope() {
				return faEnvelope;
			},
			pickupServiceType() {
				return PickupServiceType
			},
			deliveryServiceType() {
				return DeliveryServiceType;
			},
			mobile() {
				return Mobile;
			},
			email() {
				return faEnvelope;
			},
			phone() {
				return faPhone;
			},
			userEdit() {
				return faUserEdit;
			},
			userCircle() {
				return faUserCircle;
			},
			userGroup() {
				return faUsers;
			},
			account() {
				return Account;
			},
			tpLogo() {
				return TPLogo;
			},
		},
		methods: {
			setIcon(useIcon) {
				const iconFunction = this[useIcon];
				if (isEmpty(iconFunction) || !iconFunction) {
					return this["radioButtonSelected"];
				}
				return this[useIcon];
			},
			emitClick(event) {
				this.$emit("click", event);
			},
			resolveSize(size) {
				if (!size) {
					return '1x';
				}
				return size;
			},
			style(type) {
				if (!type) {
					return;
				}
				const isStandard = (type === 'standard');
				const classes = {
					standard: isStandard,
				};
				classes[type] = true;
				return classes;
			},
		},
	}
</script>
<style lang="scss" scoped>
	@import '@style-vars';
	.icon {
		background-color: $landingPageBackground;
		border-radius: 51%;
	}
	span {
		&.transparent {
			background-color: transparent;
		}
	}
	.icon-container {
		&.overlay {
			position: absolute;
		}
		&.quick-add-icon {
			position: relative;
			top: 2px;
		}
	}
	@include desktop {
		.icon-container {
			&.quick-add-icon {
				font-size: 1.3em;
				transition: none;
			}
		}
	}
	span {
		&.cart {
			margin-top: 0.35rem;
			margin-right: 0.35rem;
			position: relative;

			fill: white;
			background-color: transparent;
		}
	}
	.spin-active {
		svg {
			animation: spin 1s infinite;
		}
	}
	.tracker {
		position: relative;
		color: $storeOptionTextColor;
		font-size: 1.8em;
		&.cart-icon {
			left: -1px;
			font-size: 1.6em;
		}
		&.bag-icon {
			top: -1px;
			left: 1px;
			font-size: 1.4em;
		}
		&.glove-icon {
			top: 2px;
		}
		&.delivering-icon {
			left: 1px;
		}
		&.active-pos, &.current-pos {
			color: white;
		}
	}

	.flex-end {
		display: flex;
		justify-content: flex-end;
		position: relative;
		&.modal-close-button {
			padding: 0.5em;
		}
	}

	@include mobile {
		.size--modular {
			font-size: 1.2rem;
		}
	}
</style>



// WEBPACK FOOTER //
// src/components/common/BaseIcon.vue